import type {
  PublicQuoteParams,
  AcceptQuoteParams,
  AcceptAndPayParams,
  AcceptAndPayResponse,
  ConfirmPaymentParams,
  ConfirmPaymentResponse,
  PricingBreakdownResponse,
  FleetConfigResponse,
  QuoteCouponParams,
  ValidateQuoteCouponResponse,
  ApplyQuoteCouponResponse,
  PublicCouponError,
  QuoteAcceptanceDocumentsResponse,
  QuoteScreeningQuestionsResponse,
} from "./types";
import type { Response } from "../../types";
import type {
  PublicQuoteRenterDriverInput,
  PublicQuoteRenterDrivers,
} from "../renter-drivers/types";
import type { DocumentAcceptanceEvidence } from "../soft-holds/types";

/**
 * Helper function for public API calls (no authentication required)
 */
async function publicApiCall<T = any>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const baseUrl = process.env.NEXT_PUBLIC_API_URL || "http://localhost:4007/v1";

  const headers = {
    "Content-Type": "application/json",
    ...(options.headers || {}),
  };

  const res = await fetch(`${baseUrl}${path}`, {
    ...options,
    headers,
  });

  if (res.status === 204) {
    return {} as T;
  }

  if (!res.ok) {
    const errorText = await res.text();
    let errorMessage = errorText;
    let errorJson;
    try {
      errorJson = JSON.parse(errorText);
      errorMessage = errorJson.message || errorText;
    } catch {
      // Keep original error text if not JSON
    }
    const error = new Error(errorMessage);
    (error as any).response = {
      status: res.status,
      data: errorJson || errorText,
    };
    throw error;
  }

  if (res.headers.get("content-type")?.includes("application/json")) {
    return res.json() as T;
  } else {
    return res.text() as unknown as T;
  }
}

/**
 * Get quote details for review (public endpoint, no auth required)
 */
export const getQuoteForReview = async ({
  quoteId,
  token,
}: PublicQuoteParams) => {
  const params = new URLSearchParams({ token });
  return publicApiCall<Response<any>>(
    `/public/quotes/${quoteId}?${params.toString()}`,
  );
};

/**
 * Accept a quote (public endpoint, no auth required)
 */
export const acceptQuote = async ({ quoteId, token }: AcceptQuoteParams) => {
  return publicApiCall<Response<any>>(`/public/quotes/${quoteId}/accept`, {
    method: "POST",
    body: JSON.stringify({ token }),
  });
};

/**
 * Accept a quote and process payment (public endpoint, no auth required)
 */
export const acceptAndPay = async ({
  quoteId,
  token,
  payment,
  agreed_documents,
  deposit_acknowledged,
}: AcceptAndPayParams): Promise<AcceptAndPayResponse> => {
  return publicApiCall<AcceptAndPayResponse>(
    `/public/quotes/${quoteId}/accept-and-pay`,
    {
      method: "POST",
      body: JSON.stringify({
        token,
        payment,
        agreed_documents,
        deposit_acknowledged,
      }),
    },
  );
};

/**
 * Confirm payment after 3D Secure authentication (public endpoint, no auth required)
 */
export const confirmPayment = async ({
  quoteId,
  token,
  payment_id,
}: ConfirmPaymentParams): Promise<ConfirmPaymentResponse> => {
  return publicApiCall<ConfirmPaymentResponse>(
    `/public/quotes/${quoteId}/confirm-payment`,
    {
      method: "POST",
      body: JSON.stringify({ token, payment_id }),
    },
  );
};

/**
 * Retry payment for a quote when previous payment failed (public endpoint, no auth required)
 */
export const retryPayment = async ({
  quoteId,
  token,
  payment,
  deposit_acknowledged,
}: AcceptAndPayParams): Promise<AcceptAndPayResponse> => {
  return publicApiCall<AcceptAndPayResponse>(
    `/public/quotes/${quoteId}/retry-payment`,
    {
      method: "POST",
      body: JSON.stringify({ token, payment, deposit_acknowledged }),
    },
  );
};

/**
 * Get pricing breakdown for a quote (public endpoint, no auth required)
 */
export const getQuotePricingBreakdown = async ({
  quoteId,
  token,
}: PublicQuoteParams): Promise<PricingBreakdownResponse> => {
  return publicApiCall<PricingBreakdownResponse>(
    `/public/quotes/${quoteId}/pricing-breakdown?token=${encodeURIComponent(token)}`,
    {
      method: "GET",
    },
  );
};

/**
 * Get fleet configuration including Stripe account (public endpoint, no auth required)
 */
export const getFleetConfig = async ({
  token,
}: Pick<PublicQuoteParams, "token">): Promise<FleetConfigResponse> => {
  return publicApiCall<FleetConfigResponse>(
    `/public/config?token=${encodeURIComponent(token)}`,
    {
      method: "GET",
    },
  );
};

/**
 * Create a soft hold from a quote (public endpoint, no auth required)
 * This replaces acceptAndPay for the Payment Intent-first flow
 *
 * `accepted_document_version_ids` must be the exact `document_version_id`s
 * returned by `getQuoteAcceptanceDocuments` that the customer explicitly
 * checked — never a document's own id, never the legacy `agreed_documents`
 * ids. The server reads this at the top level
 * (`PublicQuoteController.create_hold/2`), the same field name and
 * placement as the widget's soft-hold request.
 */
export const createSoftHoldFromQuote = async ({
  quoteId,
  token,
  sms_opt_in,
  deposit_acknowledged,
  accepted_document_version_ids,
  document_form_data,
  document_evidence,
  screening_answers,
}: PublicQuoteParams & {
  sms_opt_in?: boolean;
  deposit_acknowledged?: boolean;
  accepted_document_version_ids?: string[];
  /**
   * Values the renter typed inside each document, keyed by
   * `document_version_id` (temporary capture of fleet HTML forms).
   */
  document_form_data?: Record<string, Record<string, string>>;
  /**
   * Client-side hashes and context per document version (SHA-256 of the
   * content shown and of the typed values, locale, timezone, viewport, view
   * times). A cross-check only: the server recomputes the authoritative
   * hashes from what it stores.
   */
  document_evidence?: Record<string, DocumentAcceptanceEvidence>;
  screening_answers?: Record<string, string>;
}): Promise<{
  data: {
    hold_id: string;
    hold_token: string;
    client_secret: string;
    expires_at: string;
  };
}> => {
  return publicApiCall<{
    data: {
      hold_id: string;
      hold_token: string;
      client_secret: string;
      expires_at: string;
    };
  }>(`/public/quotes/${quoteId}/create-hold`, {
    method: "POST",
    body: JSON.stringify({
      token,
      sms_opt_in,
      deposit_acknowledged,
      ...(screening_answers ? { screening_answers } : {}),
      accepted_document_version_ids: accepted_document_version_ids ?? [],
      ...(document_form_data ? { document_form_data } : {}),
      ...(document_evidence && Object.keys(document_evidence).length > 0
        ? { document_evidence }
        : {}),
    }),
  });
};

/**
 * Lists the mandatory (checkbox) documents a customer must accept before a
 * soft hold can be created from this quote (fleet-agreement-acceptance,
 * path 2's read side). Same shape as
 * `GET /vehicles/:vehicle_id/acceptance-documents`
 * (`FmsApi.AcceptanceDocumentView` "index.json"), scoped to the quote's
 * vehicle and authenticated by the quote's own access token rather than a
 * fleet session or API key.
 */
export const getQuoteAcceptanceDocuments = async ({
  quoteId,
  token,
}: PublicQuoteParams): Promise<QuoteAcceptanceDocumentsResponse> => {
  return publicApiCall<QuoteAcceptanceDocumentsResponse>(
    `/public/quotes/${quoteId}/acceptance-documents?token=${encodeURIComponent(token)}`,
  );
};

/**
 * Get the active instant-booking questions associated with a public quote.
 * The quote token scopes the request; fleet and flow are resolved server-side.
 */
export const getQuoteScreeningQuestions = async ({
  quoteId,
  token,
}: PublicQuoteParams): Promise<QuoteScreeningQuestionsResponse> => {
  return publicApiCall<QuoteScreeningQuestionsResponse>(
    `/public/quotes/${quoteId}/screening-questions?token=${encodeURIComponent(token)}`,
  );
};

/**
 * Check a discount code without redeeming it (public endpoint, token-based).
 *
 * Read-only: the server writes nothing, so a rejection leaves the quote — and
 * therefore the cart, the totals and the checkout button — exactly as it was.
 */
export const validateQuoteCoupon = async ({
  quoteId,
  token,
  code,
}: QuoteCouponParams): Promise<ValidateQuoteCouponResponse> => {
  return publicApiCall<ValidateQuoteCouponResponse>(
    `/public/quotes/${quoteId}/validate-coupon`,
    {
      method: "POST",
      body: JSON.stringify({ token, code }),
    },
  );
};

/**
 * Redeem a discount code against a quote (public endpoint, token-based).
 *
 * Returns the customer-facing discount and the re-priced quote total. Callers
 * should refetch the pricing breakdown afterwards rather than patching the
 * total locally, so the credit line and the total always agree.
 */
export const applyQuoteCoupon = async ({
  quoteId,
  token,
  code,
}: QuoteCouponParams): Promise<ApplyQuoteCouponResponse> => {
  return publicApiCall<ApplyQuoteCouponResponse>(
    `/public/quotes/${quoteId}/apply-coupon`,
    {
      method: "POST",
      body: JSON.stringify({ token, code }),
    },
  );
};

/**
 * Drop the coupon currently applied to a quote (public endpoint, token-based).
 *
 * The token travels on the query string because this request carries no body,
 * matching the other token-authenticated GET-style public calls above.
 */
export const removeQuoteCoupon = async ({
  quoteId,
  token,
}: PublicQuoteParams): Promise<Record<string, never>> => {
  return publicApiCall<Record<string, never>>(
    `/public/quotes/${quoteId}/coupon?token=${encodeURIComponent(token)}`,
    {
      method: "DELETE",
    },
  );
};

/**
 * Unwrap a coded coupon rejection from an error thrown by the calls above.
 *
 * Returns `null` for anything that is not a 422 coded rejection — a network
 * blip, a 500, a malformed body — so callers can tell "the customer typed a
 * bad code" apart from "something broke", and degrade differently for each.
 */
export const parsePublicCouponError = (
  error: unknown,
): PublicCouponError | null => {
  const response = (error as { response?: unknown } | null | undefined)
    ?.response as { status?: number; data?: unknown } | undefined;

  if (!response || response.status !== 422) return null;

  const coded = (response.data as { error?: unknown } | null | undefined)
    ?.error as
    | { code?: unknown; message?: unknown; payload?: unknown }
    | undefined;

  if (!coded || typeof coded.code !== "string") return null;

  return {
    code: coded.code,
    message: typeof coded.message === "string" ? coded.message : "",
    payload:
      (coded.payload as Record<string, unknown> | undefined) ?? undefined,
  };
};

/**
 * Get a confirmed rental (public endpoint, token-based).
 *
 * Returns the rental detail plus `deposit_required` and `deposit_amount`
 * which the post-checkout UI uses to decide whether to prompt the
 * customer for a credit card to back the security deposit.
 */
export const getPublicRental = async ({
  rentalId,
  token,
}: {
  rentalId: string;
  token: string;
}): Promise<{
  data: {
    id: string;
    status: string;
    total_amount: string;
    deposit_required: boolean;
    deposit_amount: string | null;
    deposit_hold_in_progress?: boolean;
    /**
     * Set by the backend (webhook or admin booking path) once it has finished
     * its automatic deposit-hold attempt — succeeded, skipped, or failed.
     * While this is `null`, the embed UI must wait; once set, the UI can
     * decide what to do based on `deposit_required` + `deposit_hold_in_progress`.
     */
    deposit_hold_attempted_at?: string | null;
    /**
     * ISO8601 time (UTC) at which the backend plans the automatic deposit
     * hold, or `null` when none is planned. Optional so older backends keep
     * working.
     */
    deposit_hold_scheduled_at?: string | null;
    [key: string]: unknown;
  };
}> => {
  const params = new URLSearchParams({ token });
  return publicApiCall(`/public/rentals/${rentalId}?${params.toString()}`);
};

/**
 * Create a security-deposit hold for a confirmed rental (public, token-based).
 *
 * The supplied PaymentMethod must back a credit card; the backend rejects
 * debit/prepaid cards with `code: "deposit_card_must_be_credit"`.
 */
export const createPublicDepositHold = async ({
  rentalId,
  token,
  stripe_payment_method_id,
}: {
  rentalId: string;
  token: string;
  stripe_payment_method_id: string;
}): Promise<{
  data: {
    /** Present when a hold was created; absent when `status` is `scheduled`. */
    id?: string;
    /** `scheduled` means no hold was placed because the fleet's hold is not due yet. */
    status: string;
    amount?: string;
    stripe_payment_intent_id?: string | null;
    /** ISO8601 time (UTC) at which the hold will be placed; set with `scheduled`. */
    scheduled_at?: string | null;
  };
}> => {
  return publicApiCall(`/public/rentals/${rentalId}/deposit-hold`, {
    method: "POST",
    body: JSON.stringify({ token, stripe_payment_method_id }),
  });
};

/**
 * The drivers already on a quote, as the renter may see them (FLEE-442).
 * Never carries email, phone, date of birth, address or the full number.
 */
export const getPublicRenterDrivers = async ({
  quoteId,
  token,
}: PublicQuoteParams) => {
  const params = new URLSearchParams({ token });
  return publicApiCall<Response<PublicQuoteRenterDrivers>>(
    `/public/quotes/${quoteId}/renter-drivers?${params.toString()}`,
  );
};

/**
 * Fills the blanks of drivers staff already added (no add, no remove). Each
 * entry carries its profile `id` and only the keys it completes. A 422 parses
 * with `parseRenterDriverError`.
 */
export const savePublicRenterDrivers = async ({
  quoteId,
  token,
  renterDrivers,
}: PublicQuoteParams & { renterDrivers: PublicQuoteRenterDriverInput[] }) => {
  return publicApiCall<Response<PublicQuoteRenterDrivers>>(
    `/public/quotes/${quoteId}/renter-drivers`,
    {
      method: "PUT",
      body: JSON.stringify({ token, renter_drivers: renterDrivers }),
    },
  );
};
